import {
  memo, useEffect, useRef, useState,
} from 'react';
import { useSnackbar } from 'notistack';
import PermanentStoreItem from '../../Components/PermanentStoreItem';
import StoreItem from '../../Components/ShopComponents/StoreItem';
import { ShopTabs } from '../../Components/ShopComponents/tabs';
import { useAutopilotContext } from '../../providers/AutopilotProvider';
import { useAppDispatch, useAppSelector } from '../../store';
import { updateProfileCoins } from '../../store/reducers/profile/asyncActions';
import {
  buyStoreItem, finishAutopilot, getAutopilot, getPurchases, getShopItems, startAutopilot,
} from '../../store/reducers/store/asyncActions';
import { ShopItemT } from '../../types';
import StarBg from '../../assets/svg/boosters/BoostersStarBg.svg';
import { useResizeObserver } from '../../hooks/useResizeObserver';

const Boosters = memo(() => {
  const dispatch = useAppDispatch();

  const { enqueueSnackbar } = useSnackbar();

  const { time } = useAutopilotContext();

  const { shopItems, purchases } = useAppSelector(
    ({ shop }) => shop,
  );
  const { userData } = useAppSelector(
    ({ profile }) => profile,
  );
  const [activeTab, setActiveTab] = useState<typeof ShopTabs[number]>('permanent');
  const [processingItemIds, setProcessingItemIds] = useState<number[]>([]);
  const handleBuy = async (item: ShopItemT) => {
    const nextLevel = [...item.levels || []].sort((level) => level.level).find((level) => !level.purchased_at);
    if (nextLevel) {
      const isEnoughFunds = ((userData?.coins || 0) - nextLevel.price) > 0;

      if (isEnoughFunds) {
        dispatch(updateProfileCoins({ coins: -nextLevel.price, replace: false }));
        try {
          setProcessingItemIds((prev) => [...prev, item.id]);
          await dispatch(buyStoreItem({ id: item.id, level: nextLevel.level }));
        } catch (err) {
          dispatch(updateProfileCoins({ coins: nextLevel.price, replace: false }));
          enqueueSnackbar('Something went wrong', { variant: 'error' });
        } finally {
          setProcessingItemIds((prev) => prev.filter((id) => id !== item.id));
        }
      } else {
        enqueueSnackbar('You have not enough funds', {
          variant: 'error',
        });
      }
    }
  };
  useEffect(() => {
    dispatch(getShopItems());
    dispatch(getPurchases());
  }, []);
  useEffect(() => {
    if (activeTab === 'permanent') {
      dispatch(getAutopilot());
    }
  }, [activeTab]);
  const handleStartFarm = async () => {
    const resp = await dispatch(startAutopilot());

    if (!resp.error) {
      time.start(new Date(resp.payload.autopilot.started_at).getTime(), new Date(resp.payload.autopilot.end_at).getTime());
    }
  };
  const handleCollectFarm = async () => {
    const resp = await dispatch(finishAutopilot());

    if (!resp?.error) {
      await dispatch(getAutopilot());
    }
  };

  const activeTemporaryItems = purchases?.filter((el) => el.store_item_level.duration_seconds && el.expires_at && el.is_active) || [];
  const activeTemporaryItemIds = (activeTemporaryItems || []).map((tempItem) => tempItem.store_item_level.id);
  type GroupedData = {
    [categoryId: number]: {
      name: ShopItemT['category']['name']
      items: ShopItemT[]
    }
  }
  const groupByCategory = (items: ShopItemT[] | undefined) => items?.reduce<GroupedData>((acc: GroupedData, item) => {
    const categoryId = item.category.id;
    if (!acc[categoryId]) {
      acc[categoryId] = {
        name: item.category.name,
        items: [],
      };
    }
    acc[categoryId].items.push(item);
    return acc;
  }, {});
  const groupedData = groupByCategory(shopItems?.temporary);
  const containerRef = useRef<HTMLDivElement>(null);

  const rect = useResizeObserver(containerRef);

  useEffect(() => {
    if (!containerRef.current || !rect) {
      return;
    }
    containerRef.current.style.maxHeight = `${window.innerHeight - 50 - rect.y}px`;
  }, [rect]);
  return (
    <div className="flex flex-col pt-[.4rem] bg-questsPageBG absolute h-screen w-screen top-0 backdrop-blur-[40px] px-4">
      <div className="mt-[68px] relative min-h-[54px] z-[-1]">
        <img
          className="absolute top-1/2 left-1/2 transform -translate-y-1/2 -translate-x-1/2"
          src={StarBg}
          alt="treasures-bg"
        />
        <span className="absolute bottom-[6px] left-1/2 transform -translate-x-1/2 text-[22px] font-Bangers">
          Boosters
        </span>
      </div>
      <div className="flex justify-between bg-black-light-bg rounded-10 h-[34px] mt-[7px]">
        {ShopTabs.map((tab) => (
          <div
            key={tab}
            className={`btn ${activeTab === tab ? 'neutral' : 'purple'} rounded-8`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </div>
        ))}
      </div>
      <div className="mt-3 text-start pb-[60px] gap-2 flex flex-col max-h-[400px] overflow-y-scroll scrollbar-hidden" ref={containerRef}>
        {activeTab === 'temporary'
              && Object.entries(groupedData || {}).map(([categoryId, categoryData]) => (
                <div key={categoryId} className="gap-2 flex flex-col">
                  {/* <div className="ml-1 text-lg font-bold">{categoryData.name}</div> */}
                  {categoryData.items.map((item: ShopItemT) => (
                    <div key={item.id}>
                      <StoreItem
                        item={item}
                        onBuy={handleBuy}
                        activeTemporaryItems={activeTemporaryItems}
                        activeTemporaryItemIds={activeTemporaryItemIds}
                        processingItemIds={processingItemIds}
                      />
                    </div>
                  ))}
                </div>
              ))}
        {activeTab === 'permanent'
              && shopItems?.[activeTab].map((storeItem) => (
                <div key={storeItem.id}>
                  <PermanentStoreItem
                    item={storeItem}
                    onBuy={handleBuy}
                    onStartFarm={handleStartFarm}
                    onCollectFarm={handleCollectFarm}
                    processingItemIds={processingItemIds}
                  />
                </div>
              ))}
      </div>
    </div>
  );
});
Boosters.displayName = 'Boosters';
export default Boosters;
